/**
 * Diagram browser helpers
 *
 * Pure logic shared by the Diagram_Browser components (`DiagramsInterface`,
 * `DiagramsSidebar`, `DiagramGrid`, `DiagramTable`): folder grouping with
 * counts, the client-side search (title and folder, case-insensitive), and
 * sorting by last edited or title (Requirements 9.1, 9.4). Keeping this out of
 * the components lets it be unit-tested without rendering.
 */

import type { DiagramKind, DiagramSummary } from "@/services/diagramsService"

/** The special sidebar selection meaning "show every diagram". */
export const FOLDER_ALL: string = "__all__"

/** The special sidebar selection meaning "diagrams with no folder". */
export const FOLDER_UNFILED: string = "__unfiled__"

/** The sort orders offered in the toolbar. */
export type DiagramSortKey = "updated" | "title"

/** The default sort order: most recently edited first (Requirement 9.4). */
export const DEFAULT_SORT: DiagramSortKey = "updated"

/** The default view mode: the thumbnail grid (Requirement 9.2). */
export const DEFAULT_VIEW: DiagramView = "grid"

/**
 * The Diagram_Kind filter in the browser toolbar: a specific kind, or "all" to
 * show both (Requirement 6.3).
 */
export type DiagramKindFilter = "all" | DiagramKind

/** The default kind filter: show diagrams of every kind. */
export const DEFAULT_KIND_FILTER: DiagramKindFilter = "all"

/** The two Diagram_Browser view modes. */
export type DiagramView = "grid" | "table"

/**
 * One entry in the sidebar's folder list: either a real Diagram_Folder, or one
 * of the two special selections (All diagrams, Unfiled), each with its count.
 */
export interface FolderEntry {
  /**
   * The selection value stored in the URL and compared against the active
   * folder: `FOLDER_ALL`, `FOLDER_UNFILED`, or a real folder name.
   */
  value: string
  /** The label shown in the sidebar. */
  label: string
  /** How many diagrams this entry covers. */
  count: number
}

/**
 * Compare two Diagram_Folders one Folder_Segment at a time, so a folder sorts
 * directly before its own subfolders (for example `data`,
 * `data/routine-surveillance`, `data/zz`, `data-eng`). Segments are compared
 * with `localeCompare`; when one folder's segments are a prefix of the other's,
 * the shorter folder sorts first (Requirement 6.2).
 *
 * @param left - The first Diagram_Folder (a `/`-joined path).
 * @param right - The second Diagram_Folder (a `/`-joined path).
 * @returns A negative number if `left` sorts first, positive if `right` sorts
 *   first, and 0 when the folders are equal.
 */
export function compareFolders(left: string, right: string): number {
  const leftSegments: string[] = left.split("/")
  const rightSegments: string[] = right.split("/")
  const sharedLength: number = Math.min(leftSegments.length, rightSegments.length)
  for (let index: number = 0; index < sharedLength; index += 1) {
    const segmentOrder: number = leftSegments[index].localeCompare(rightSegments[index])
    if (segmentOrder !== 0) {
      return segmentOrder
    }
  }
  // Every shared segment is equal, so the shorter path (the parent) sorts first.
  return leftSegments.length - rightSegments.length
}

/**
 * Build the sidebar's folder list from every diagram: "All diagrams" and
 * "Unfiled" first, then each real Diagram_Folder ordered segment by segment so
 * a parent sits directly above its subfolders (see {@link compareFolders}),
 * each with its diagram count (Requirements 9.1, 6.1, 6.2).
 *
 * @param params - The inputs.
 * @param params.diagrams - Every diagram in the repo (unfiltered).
 * @returns The folder entries in display order.
 */
export function buildFolderEntries({ diagrams }: { diagrams: DiagramSummary[] }): FolderEntry[] {
  // Count diagrams per real folder, and how many are unfiled.
  const countsByFolder: Map<string, number> = new Map<string, number>()
  let unfiledCount: number = 0

  for (const diagram of diagrams) {
    if (diagram.folder === null) {
      unfiledCount += 1
      continue
    }
    countsByFolder.set(diagram.folder, (countsByFolder.get(diagram.folder) ?? 0) + 1)
  }

  const folderEntries: FolderEntry[] = [...countsByFolder.entries()]
    .sort(([leftFolder], [rightFolder]) => compareFolders(leftFolder, rightFolder))
    .map(([folder, count]): FolderEntry => ({ value: folder, label: folder, count }))

  return [
    { value: FOLDER_ALL, label: "All diagrams", count: diagrams.length },
    { value: FOLDER_UNFILED, label: "Unfiled", count: unfiledCount },
    ...folderEntries,
  ]
}

/**
 * Keep only the diagrams in the selected folder.
 *
 * @param params - The inputs.
 * @param params.diagrams - The diagrams to filter.
 * @param params.folder - The active selection (`FOLDER_ALL`, `FOLDER_UNFILED`,
 *   or a real folder name).
 * @returns The diagrams matching the selection.
 */
export function filterByFolder({
  diagrams,
  folder,
}: {
  diagrams: DiagramSummary[]
  folder: string
}): DiagramSummary[] {
  if (folder === FOLDER_ALL) {
    return diagrams
  }
  if (folder === FOLDER_UNFILED) {
    return diagrams.filter((diagram) => diagram.folder === null)
  }
  return diagrams.filter((diagram) => diagram.folder === folder)
}

/**
 * Keep only the diagrams of the selected Diagram_Kind (Requirement 6.3). The
 * "all" selection matches every kind.
 *
 * @param params - The inputs.
 * @param params.diagrams - The diagrams to filter.
 * @param params.kind - The active kind filter ("all", "excalidraw", "mermaid").
 * @returns The diagrams matching the selected kind.
 */
export function filterByKind({
  diagrams,
  kind,
}: {
  diagrams: DiagramSummary[]
  kind: DiagramKindFilter
}): DiagramSummary[] {
  if (kind === "all") {
    return diagrams
  }
  return diagrams.filter((diagram) => diagram.kind === kind)
}

/**
 * Keep only the diagrams whose title or folder contains the query, matched
 * case-insensitively (Requirement 9.4). An empty or whitespace-only query
 * matches everything.
 *
 * @param params - The inputs.
 * @param params.diagrams - The diagrams to filter.
 * @param params.query - The raw search query.
 * @returns The matching diagrams.
 */
export function filterBySearch({
  diagrams,
  query,
}: {
  diagrams: DiagramSummary[]
  query: string
}): DiagramSummary[] {
  const needle: string = query.trim().toLowerCase()
  if (needle === "") {
    return diagrams
  }
  return diagrams.filter((diagram) => {
    const title: string = diagram.title.toLowerCase()
    const folder: string = (diagram.folder ?? "").toLowerCase()
    return title.includes(needle) || folder.includes(needle)
  })
}

/**
 * Sort diagrams by the chosen key, returning a new array (the input is not
 * mutated). "updated" orders by last edited time, newest first; "title" orders
 * alphabetically, case-insensitively (Requirement 9.4).
 *
 * @param params - The inputs.
 * @param params.diagrams - The diagrams to sort.
 * @param params.sort - The sort key.
 * @returns A new, sorted array.
 */
export function sortDiagrams({
  diagrams,
  sort,
}: {
  diagrams: DiagramSummary[]
  sort: DiagramSortKey
}): DiagramSummary[] {
  const sorted: DiagramSummary[] = [...diagrams]
  if (sort === "title") {
    sorted.sort((left, right) =>
      left.title.localeCompare(right.title, undefined, { sensitivity: "base" }),
    )
  } else {
    // "updated": newest first. updatedAt is an ISO 8601 string, which sorts
    // chronologically as text, but parse to be explicit and robust.
    sorted.sort(
      (left, right) =>
        new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime(),
    )
  }
  return sorted
}

/**
 * Apply the folder selection, search query, and sort order in one step, as the
 * Diagram_Browser does to turn the full list into what the grid or table shows.
 *
 * @param params - The inputs.
 * @param params.diagrams - Every diagram in the repo.
 * @param params.folder - The active folder selection.
 * @param params.query - The search query.
 * @param params.sort - The sort key.
 * @returns The filtered, sorted diagrams.
 */
export function selectDiagrams({
  diagrams,
  folder,
  kind,
  query,
  sort,
}: {
  diagrams: DiagramSummary[]
  folder: string
  kind: DiagramKindFilter
  query: string
  sort: DiagramSortKey
}): DiagramSummary[] {
  const byFolder: DiagramSummary[] = filterByFolder({ diagrams, folder })
  const byKind: DiagramSummary[] = filterByKind({ diagrams: byFolder, kind })
  const bySearch: DiagramSummary[] = filterBySearch({ diagrams: byKind, query })
  return sortDiagrams({ diagrams: bySearch, sort })
}

/**
 * Narrow an untrusted string to a valid {@link DiagramView}, falling back to
 * the default. Used when reading the `view` URL parameter.
 *
 * @param value - The raw string (e.g. from a URL parameter), or null.
 * @returns The view mode, defaulting to {@link DEFAULT_VIEW}.
 */
export function parseView(value: string | null): DiagramView {
  return value === "grid" || value === "table" ? value : DEFAULT_VIEW
}

/**
 * Narrow an untrusted string to a valid {@link DiagramSortKey}, falling back to
 * the default. Used when reading the `sort` URL parameter.
 *
 * @param value - The raw string (e.g. from a URL parameter), or null.
 * @returns The sort key, defaulting to {@link DEFAULT_SORT}.
 */
export function parseSort(value: string | null): DiagramSortKey {
  return value === "title" || value === "updated" ? value : DEFAULT_SORT
}

/**
 * Narrow an untrusted string to a valid {@link DiagramKindFilter}, falling back
 * to the default. Used when reading the `kind` URL parameter.
 *
 * @param value - The raw string (e.g. from a URL parameter), or null.
 * @returns The kind filter, defaulting to {@link DEFAULT_KIND_FILTER} ("all").
 */
export function parseKindFilter(value: string | null): DiagramKindFilter {
  return value === "excalidraw" || value === "mermaid" ? value : DEFAULT_KIND_FILTER
}
